import type { ProductId } from "../components/brand";

/** Best-effort mapping from an application/client name to a product brand mark. */
export function productIdFromName(name: string): ProductId | null {
  const n = name.toLowerCase();
  if (n.includes("bildungsplattform")) return "bp";
  if (n.includes("planung")) return "planung";
  if (n.includes("zeugnis")) return "zeugnis";
  if (n.includes("nextcloud")) return "nextcloud";
  return null;
}

const PRODUCT_LABEL: Record<ProductId, string> = {
  bp: "Bildungsplattform",
  planung: "Planung",
  zeugnis: "Zeugnis",
  nextcloud: "Nextcloud",
};

/** Human-readable product name for a product id. */
export function productLabel(id: ProductId): string {
  return PRODUCT_LABEL[id];
}

/** Best-effort mapping from an OAuth client id (e.g. "bp-frontend-client") to a product. */
export function productIdFromClientId(clientId: string): ProductId | null {
  const c = clientId.toLowerCase();
  if (c.includes("zeugnis")) return "zeugnis";
  if (c.includes("planung")) return "planung";
  if (c.includes("nextcloud") || c.startsWith("cloud_")) return "nextcloud";
  if (c.includes("bp")) return "bp";
  return null;
}

/**
 * Display name of an application: its own name unless that is just the client
 * id (some Application nodes are seeded that way), then the product label, then
 * the raw id.
 */
export function appDisplayName(name: string | null | undefined, clientId: string): string {
  if (name && name !== clientId && !looksLikeMachineId(name)) {
    return name;
  }
  const pid = productIdFromClientId(clientId) ?? (name ? productIdFromClientId(name) : null);
  return pid ? productLabel(pid) : name || clientId || "—";
}

/** "cloud_565ecafb-6d73-…": an id with a uuid in it, not a name meant for humans. */
function looksLikeMachineId(value: string): boolean {
  return /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(value);
}

/** Distinct products an organization has licensed, derived from its licensed-app client ids. */
export function productsFromClientIds(
  clientIds: readonly (string | null | undefined)[],
): ProductId[] {
  const seen = new Set<ProductId>();
  for (const id of clientIds) {
    const pid = id ? productIdFromClientId(id) : null;
    if (pid) {
      seen.add(pid);
    }
  }
  return [...seen].sort((a, b) => productLabel(a).localeCompare(productLabel(b), "de"));
}

/**
 * Product a frontend URL belongs to (e.g. `User.invitationFrontendUrl`), by
 * host name. Best effort like the other mappings here.
 */
export function productIdFromUrl(url: string | null | undefined): ProductId | null {
  if (!url) {
    return null;
  }
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host.includes("zeugnis")) return "zeugnis";
  if (host.includes("planung")) return "planung";
  if (host.includes("nextcloud")) return "nextcloud";
  if (host.split(".")[0] === "bp" || host.includes("bildungsplattform")) return "bp";
  return null;
}

/**
 * bp-app-client is a duplicate registration of bp-frontend-client (see
 * pim-auth-server accounts.ts, EXCLUDED_APPLICATION_CLIENT_IDS): one product.
 */
const CLIENT_ALIASES: Record<string, string> = { "bp-app-client": "bp-frontend-client" };

/** The client id a duplicate registration stands for. */
export function canonicalClientId(clientId: string): string {
  return CLIENT_ALIASES[clientId] ?? clientId;
}

/** API clients are licensed per organization but never something a person signs in to. */
export function isUserFacingClient(clientId: string): boolean {
  return !clientId.endsWith("-api-client");
}

/**
 * URL of the application per invitation product, from the apps' `uri`. The
 * invitation carries it as its frontend URL: after the signup the person lands
 * in that tool, and the confirmation mail links there. Products whose apps
 * carry no URI are left out; the caller then falls back to the auth-server.
 */
export function invitationProductUrls(
  apps: readonly { clientId?: string | null; uri?: string | null }[],
): Partial<Record<"bp" | "planung", string>> {
  const out: Partial<Record<"bp" | "planung", string>> = {};
  for (const app of apps) {
    const pid = app.clientId ? productIdFromClientId(canonicalClientId(app.clientId)) : null;
    if ((pid === "bp" || pid === "planung") && app.uri && !out[pid]) {
      out[pid] = app.uri;
    }
  }
  return out;
}

/** Invitation products a profile can be invited to, from the apps it can reach. */
export function invitationProductsFromClientIds(
  clientIds: readonly (string | null | undefined)[],
): ("bp" | "planung")[] {
  const out = new Set<"bp" | "planung">();
  for (const id of clientIds) {
    const pid = id ? productIdFromClientId(canonicalClientId(id)) : null;
    if (pid === "bp" || pid === "planung") {
      out.add(pid);
    }
  }
  return [...out];
}
